import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core'
import { trigger, state, style, transition, animate } from '@angular/animations'
import { NavigationEnd, Router } from '@angular/router'
import { Subject, combineLatest } from 'rxjs'
import { filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { SyncPhaseDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { CrudAction, CrudEvent } from 'src/utils/crud-subject'
import { handleArpioError } from 'src/utils/handle-error'
import { AccountService } from '../../services/account.service'
import { Application, ApplicationProviderService } from '../../services/application.service'
import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'
import { OnboardingService } from '../../services/onboarding.service'

interface ApplicationDetail {
  id: string
  name: string
  url: string
  syncPhase: SyncPhaseDto
  issueCount: number
  isDeleted?: boolean
  sourceAwsAccountId?: string
  sourceRegion?: string
  targetAwsAccountId?: string
  targetRegion?: string
}

@Component({
  selector: 'account-navigation',
  templateUrl: './account-navigation.component.html',
  styleUrls: ['./account-navigation.component.scss'],
  animations: [
    trigger('slideDown', [
      state(
        'void',
        style({
          height: '0',
          opacity: '0',
          overflow: 'hidden',
        })
      ),
      state(
        '*',
        style({
          height: '*',
          opacity: '1',
          overflow: 'hidden',
        })
      ),
      transition('void <=> *', [animate('200ms ease-in-out')]),
    ]),
    trigger('fadeIn', [
      transition(':enter', [style({ opacity: 0 }), animate('250ms 100ms ease-in', style({ opacity: 1 }))]),
      transition(':leave', [style({ display: 'none' })]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountNavigationComponent implements OnInit, OnDestroy, OnChanges {
  @Input('account-id') accountId: string = null
  @Input('initialized') initialized: boolean = false

  // Internal state to ensure smooth loading transitions
  // This stays false briefly after initialized becomes true to prevent loading flicker
  public readyToShowContent: boolean = false

  constructor(
    private accountService: AccountService,
    private apiService: ApiService,
    public uiService: UiService,
    private applicationProviderService: ApplicationProviderService,
    private accessLevelService: AccessLevelService,
    private router: Router,
    private onboardingService: OnboardingService
  ) {}

  private applications: ApplicationDetail[] | null = null
  private destroy$ = new Subject<void>()

  // Track applications loading state independently
  private applicationsLoading = false

  // Permission states
  private hasReadRoleAccess = false
  private canCreateApp = false
  private canReadBillableResourceReport = false

  // localStorage key for the persisted desktop sidebar collapsed state.
  private readonly SIDEBAR_COLLAPSED_KEY = 'arpio-sidebar-collapsed'
  // Sidebar collapsed state — persisted to localStorage so it survives page refreshes.
  public sidebarCollapsed = false

  // Mobile navigation state
  public isMobile = window.innerWidth <= 800
  public mobileMenuOpen = false

  // Search filter
  public searchTerm = ''
  private preSearchSections: {
    applications: boolean
    accessManagement: boolean
    account: boolean
    help: boolean
  } | null = null

  public sections = {
    applications: window.innerWidth > 800,
    accessManagement: window.innerWidth > 800,
    account: false,
    help: false,
  }

  @HostListener('window:resize')
  onResize(): void {
    const wasMobile = this.isMobile
    this.isMobile = window.innerWidth <= 800
    // Reset mobile menu state when switching to desktop
    if (wasMobile && !this.isMobile) {
      this.mobileMenuOpen = false
    }
  }

  ngOnInit(): void {
    const savedState = localStorage.getItem(this.SIDEBAR_COLLAPSED_KEY)
    if (savedState !== null) {
      this.sidebarCollapsed = savedState === 'true'
    }

    // Initialize permissions on component init
    this.initializePermissions()

    // Monitor router events to track when child route navigation completes
    // Show content immediately when navigation finishes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        // After navigation ends, if we're initialized and waiting to show content, show it immediately
        if (this.initialized && !this.readyToShowContent) {
          this.readyToShowContent = true
        }

        // Auto-expand the relevant section based on current route
        this.autoExpandSectionForRoute(event.urlAfterRedirects)
        // Auto-close mobile menu on navigation
        if (this.isMobile && this.mobileMenuOpen) {
          this.mobileMenuOpen = false
        }
      })
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen
    // When opening mobile menu, expand all sections for easier navigation
    if (this.mobileMenuOpen) {
      this.sections.applications = true
      this.sections.account = false
      this.sections.help = false
    }
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed
    localStorage.setItem(this.SIDEBAR_COLLAPSED_KEY, String(this.sidebarCollapsed))

    // When expanding the sidebar, open applications and close others
    if (!this.sidebarCollapsed) {
      // Close all sections immediately
      this.sections.applications = false
      this.sections.accessManagement = false
      this.sections.account = false
      this.sections.help = false

      // Wait for sidebar to expand and nav-content to fade in before opening applications
      setTimeout(() => {
        this.sections.applications = true
      }, 350) // Wait for sidebar width transition (300ms) + fadeIn delay (100ms)
    }
  }

  expandSidebarAndSection(section: 'account' | 'help'): void {
    this.sidebarCollapsed = false
    localStorage.setItem(this.SIDEBAR_COLLAPSED_KEY, String(this.sidebarCollapsed))

    // Collapse all sections first
    this.sections.applications = false
    this.sections.accessManagement = false
    this.sections.account = false
    this.sections.help = false

    // Wait for sidebar to expand and nav-content to fade in before expanding the section
    setTimeout(() => {
      this.sections[section] = true
    }, 350) // Wait for sidebar width transition (300ms) + fadeIn delay (100ms)
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions when component is destroyed
    this.destroy$.next()
    this.destroy$.complete()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('accountId' in changes) {
      this.resetData()
      this.setupSubscriptions(changes.accountId.currentValue)
      // Don't reinitialize permissions on account change - they should persist

      // Reset ready state when account changes
      this.readyToShowContent = false
    }

    if ('initialized' in changes) {
      if (changes.initialized.currentValue === false) {
        // When account starts loading, hide content
        this.readyToShowContent = false
      } else if (changes.initialized.currentValue === true) {
        // When initialized becomes true, show content immediately
        this.readyToShowContent = true
      }
    }
  }

  private initializePermissions() {
    // Get the relevant user permissions
    // Use startWith to provide immediate default values before the API call completes
    combineLatest([
      this.accessLevelService.hasAccountPermission(AccountPermission.READ_ROLE).pipe(startWith(false)),
      this.accessLevelService.hasAccountPermission(AccountPermission.CREATE_APP).pipe(startWith(false)),
      this.accessLevelService
        .hasAccountPermission(AccountPermission.READ_BILLABLE_RESOURCE_REPORT)
        .pipe(startWith(false)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([hasReadRole, hasCreateApp, hasReadBillableResourceReport]) => {
        // hasReadRoleAccess is used for viewing RBAC section
        this.hasReadRoleAccess = hasReadRole
        // Only ACCOUNT_ADMIN has CREATE_APP permission
        this.canCreateApp = hasCreateApp
        this.canReadBillableResourceReport = hasReadBillableResourceReport
      })
  }

  private resetData() {
    this.cancelSubscriptions()
    this.applications = null
    // Start loading applications
    this.applicationsLoading = true
  }

  /**
   *
   * @param accountId
   */
  private setupSubscriptions(accountId: string) {
    this.accountService.accountDtoStream
      .pipe(
        takeUntil(this.destroy$),
        switchMap((accountDto) => {
          // Multi-account just has to watch for application events.  Don't need sync pair or sync source events
          return this.applicationProviderService
            .subscribeToApplications$(accountDto.accountId)
            .pipe(
              map((applicationCrudEvent) =>
                this._handleApplicationCrudEvent(accountDto.accountId, applicationCrudEvent)
              )
            )
        })
      )
      .subscribe()

    // Set a timeout to handle the case where there are no applications
    // If no CRUD events are received within a short time, assume there are no applications
    setTimeout(() => {
      if (this.applications === null) {
        this.applications = []
      }
      // Stop loading after timeout, whether we have applications or not
      this.applicationsLoading = false
      // Ensure main content is shown when we finish loading (especially for "no applications" case)
      if (this.initialized) {
        this.readyToShowContent = true
      }
    }, 1500)
  }

  private cancelSubscriptions() {
    this.destroy$.next()
  }

  private _handleApplicationCrudEvent(accountId: string, appCrudEvent: CrudEvent<Application>) {
    // Initialize applications array if it's null (first time loading)
    const isFirstLoad = this.applications === null
    if (isFirstLoad) {
      this.applications = []
    }

    // filter-out this application from our list
    this.applications = this.applications.filter((appDetail) => appDetail.id != appCrudEvent.entityId)

    // add it back in if this is an UPSERT event
    if (appCrudEvent.action == CrudAction.UPSERT) {
      const updatedApplication: ApplicationDetail = {
        id: appCrudEvent.entity.applicationId,
        name: appCrudEvent.entity.name,
        url: this.uiService.buildApplicationUrl(accountId, appCrudEvent.entity.applicationId),
        syncPhase: appCrudEvent.entity.syncPhase,
        issueCount: appCrudEvent.entity.allIssues.length,
        isDeleted: appCrudEvent.entity?.dto?.deleted,
        sourceAwsAccountId: appCrudEvent.entity.sourceAwsAccountId,
        sourceRegion: appCrudEvent.entity.sourceRegion,
        targetAwsAccountId: appCrudEvent.entity.targetAwsAccountId,
        targetRegion: appCrudEvent.entity.targetRegion,
      }
      this.applications = [...this.applications, updatedApplication]

      // Refresh user permissions to include the new application in effectiveAccessLevels
      this.accessLevelService.refreshUserPermissions()
    }

    // Stop loading when we receive the first CRUD event
    if (isFirstLoad) {
      this.applicationsLoading = false
      // Ensure main content is shown when applications finish loading
      if (this.initialized) {
        this.readyToShowContent = true
      }
    }
  }

  get loading() {
    // Keep showing loading spinner as long as either:
    // 1. Applications are still loading, OR
    // 2. Main content overlay is still showing (!readyToShowContent)
    // This ensures the side nav never shows empty space while the overlay is visible
    return this.applicationsLoading || !this.readyToShowContent
  }

  get sortedApplications(): ApplicationDetail[] {
    if (!!this.applications) {
      // Sort applications alphabetically by name
      // Note: The API already filters applications based on user permissions,
      // so we don't need to filter here - we trust the backend as the source of truth
      return this.applications.sort((app1, app2) => (app1.name < app2.name ? -1 : 1))
    }
    return []
  }

  get filteredApplications(): ApplicationDetail[] {
    const term = this.searchTerm.trim().toLowerCase()
    if (!term || this.matchesSearch('Applications')) {
      return this.sortedApplications
    }
    return this.sortedApplications.filter((app) => this.applicationMatchesSearch(app, term))
  }

  private applicationMatchesSearch(app: ApplicationDetail, term: string): boolean {
    // Match by app name or ID
    if (app.name.toLowerCase().includes(term)) return true
    if (app.id?.toLowerCase().includes(term)) return true

    // Keyword: "issues" — show only apps with issues
    if ('issues'.includes(term) && app.issueCount > 0) return true

    // Keyword: "testing" / "test" — show apps in failoverTest phase
    if (('testing'.includes(term) || 'failover test'.includes(term)) && app.syncPhase === 'failoverTest') return true

    // Keyword: "recovery" / "failover" — show apps in failover phase
    if (('recovery'.includes(term) || 'failover'.includes(term)) && app.syncPhase === 'failover') return true

    // Match by AWS Account ID (source or target)
    if (app.sourceAwsAccountId?.toLowerCase().includes(term)) return true
    if (app.targetAwsAccountId?.toLowerCase().includes(term)) return true

    // Match by AWS region (source or target)
    if (app.sourceRegion?.toLowerCase().includes(term)) return true
    if (app.targetRegion?.toLowerCase().includes(term)) return true

    return false
  }

  get isSearching(): boolean {
    return this.searchTerm.trim().length > 0
  }

  matchesSearch(label: string): boolean {
    const term = this.searchTerm.trim().toLowerCase()
    if (!term) return true
    return label.toLowerCase().includes(term)
  }

  get applicationsSectionMatch(): boolean {
    return this.matchesSearch('Applications')
  }

  get accountSectionMatch(): boolean {
    return this.matchesSearch('Account')
  }

  get helpSectionMatch(): boolean {
    return this.matchesSearch('Help')
  }

  get hasApplicationsMatch(): boolean {
    if (!this.isSearching) return true
    return (
      this.applicationsSectionMatch ||
      this.filteredApplications.length > 0 ||
      (this.canCreateApplications && this.matchesSearch('Create New ...'))
    )
  }

  get isKeywordSearch(): boolean {
    const term = this.searchTerm.trim().toLowerCase()
    if (!term) return false
    return (
      'issues'.includes(term) ||
      'testing'.includes(term) ||
      'failover test'.includes(term) ||
      'recovery'.includes(term) ||
      'failover'.includes(term)
    )
  }

  get accountSettingsMatch(): boolean {
    return (
      this.matchesSearch('Account Settings') ||
      this.matchesSearch('General') ||
      this.matchesSearch('Retention Policy') ||
      this.matchesSearch('Identity Providers') ||
      this.matchesSearch('Resource Defaults') ||
      this.matchesSearch('Tags') ||
      this.matchesSearch('API Keys')
    )
  }

  get accessManagementMatch(): boolean {
    return (
      this.matchesSearch('Access Management') ||
      this.matchesSearch('Roles') ||
      this.matchesSearch('Groups') ||
      this.matchesSearch('Users')
    )
  }

  get usageBillingMatch(): boolean {
    return this.matchesSearch('Usage') || this.matchesSearch('Billing') || this.matchesSearch('Usage & Billing')
  }

  get hasAccountMatch(): boolean {
    if (!this.isSearching) return true
    return (
      this.accountSectionMatch ||
      this.accountSettingsMatch ||
      this.accessManagementMatch ||
      (this.usageBillingVisible && this.usageBillingMatch) ||
      (this.licensedForIntegrations && this.matchesSearch('Add Ons'))
    )
  }

  get hasHelpMatch(): boolean {
    if (!this.isSearching) return true
    return this.helpSectionMatch || this.matchesSearch('Support') || this.matchesSearch('Documentation')
  }

  get hasAnySearchMatch(): boolean {
    return this.hasApplicationsMatch || this.hasAccountMatch || this.hasHelpMatch || this.matchesSearch('Sign Out')
  }

  onSearchChange(): void {
    const term = this.searchTerm.trim()

    if (term && !this.preSearchSections) {
      this.preSearchSections = { ...this.sections }
    }

    if (!term) {
      if (this.preSearchSections) {
        this.sections = { ...this.preSearchSections }
        this.preSearchSections = null
      }
      return
    }

    this.sections.applications = this.hasApplicationsMatch
    this.sections.account = this.hasAccountMatch
    this.sections.help = this.hasHelpMatch
  }

  clearSearch(): void {
    this.searchTerm = ''
    if (this.preSearchSections) {
      this.sections = { ...this.preSearchSections }
      this.preSearchSections = null
    }
  }

  get createApplicationUrl() {
    return this.uiService.buildNewApplicationUrl(this.accountService.accountId)
  }

  get accountPageUrl() {
    return this.uiService.buildAccountPageUrl(this.accountService.accountId)
  }

  get accountSettingsUrl() {
    return this.uiService.buildAccountSettingsUrl(this.accountService.accountId)
  }

  get accountSupportUrl() {
    return this.uiService.buildAccountSupportUrl(this.accountService.accountId)
  }

  get retentionPolicyUrl() {
    return this.uiService.buildRetentionPolicyUrl(this.accountService.accountId)
  }

  get accountResourceConfigsUrl() {
    return this.uiService.buildResourceConfigsUrl(this.accountService.accountId)
  }

  get integrationsUrl() {
    return this.uiService.buildIntegrationsUrl(this.accountService.accountId)
  }

  get licensedForIntegrations() {
    return this.accountService.licensedForIntegrations
  }

  get usageBillingUrl() {
    return this.uiService.buildUsageBillingUrl(this.accountService.accountId)
  }

  // Two independent gates: a phased-rollout capability granted per account, and the RBAC
  // permission the usage endpoints themselves enforce — without it the page loads and every
  // data call 403s. The page guards itself on the permission, so hiding the link isn't the
  // only defense against direct-URL access.
  get usageBillingVisible(): boolean {
    return this.accountService.usageReportsEnabled && this.canReadBillableResourceReport
  }

  get rbacEnabled() {
    return this.accountService.rbacEnabled
  }

  get rbacFullEnabled() {
    return this.accountService.rbacFullEnabled
  }

  get accessManagementUrl() {
    return this.uiService.buildAccessManagementUrl(this.accountService.accountId)
  }

  get userManagementUrl() {
    return this.uiService.buildUserManagementUrl(this.accountService.accountId)
  }

  get roleManagementUrl() {
    return this.uiService.buildRoleManagementUrl(this.accountService.accountId)
  }

  get groupManagementUrl() {
    return this.uiService.buildGroupManagementUrl(this.accountService.accountId)
  }

  get apiKeyManagementUrl() {
    return this.uiService.buildApiKeyManagementUrl(this.accountService.accountId)
  }

  get profileUrl() {
    return this.uiService.buildProfileUrl(this.accountService.accountId)
  }

  get canCreateApplications() {
    return this.canCreateApp
  }

  get canViewRbac() {
    return this.rbacEnabled && this.hasReadRoleAccess
  }

  get shouldShowApplicationsSection() {
    // Show the section if there are applications OR if the user can create new ones
    return (this.sortedApplications && this.sortedApplications.length > 0) || this.canCreateApplications
  }

  get welcomeUrl() {
    return this.uiService.buildAccountWelcomeUrl(this.accountService.accountId)
  }

  get shouldShowWelcomeLink() {
    if (!this.onboardingService.loaded || this.loading) return false
    if (this.onboardingService.introDismissed) return false
    // Only accounts seeded by visiting /welcome should surface this link. Legacy
    // accounts that incidentally got onboarding keys via mark-visited side effects
    // are not seeded and stay hidden.
    if (!this.onboardingService.isSeededOnboarding) return false
    return true
  }

  logoutClick() {
    this.apiService.logout().subscribe(
      (_null) => {
        // after logout, navigate to the root, which will re-direct to the login page
        //this.router.navigateByUrl("/");
        window.location.href = '/'
      },
      handleArpioError(window.location.href, this.apiService, this.uiService)
    )
  }

  applicationTrackBy(index: number, application: ApplicationDetail) {
    return application.id
  }

  private autoExpandSectionForRoute(url: string) {
    // Determine which section the current route belongs to and expand it
    // Close other sections to keep the nav clean
    // NOTE: Applications section is excluded from auto-expand/collapse - user has full manual control

    if (this.isApplicationRoute(url)) {
      // Don't touch applications section - let user control it
      this.sections.account = false
      this.sections.help = false
    } else if (this.isAccountRoute(url)) {
      // Don't touch applications section - let user control it
      this.sections.account = true
      this.sections.help = false
    } else if (this.isHelpRoute(url)) {
      // Don't touch applications section - let user control it
      this.sections.account = false
      this.sections.help = true
    }
    // If none match, leave sections as they are
  }

  private isApplicationRoute(url: string): boolean {
    // Check if URL belongs to applications section
    return url.includes('/applications/') || url.endsWith('/applications/new') || url.includes('/welcome')
  }

  private isAccountRoute(url: string): boolean {
    // Check if URL belongs to account section
    return url.includes('/account/') || url.endsWith('/account') || url.endsWith('/profile')
  }

  private isHelpRoute(url: string): boolean {
    // Check if URL belongs to help section
    return url.endsWith('/support')
  }

  // Angular won't re-emit identical fragments, so clear+reapply to force tab switch on repeat clicks
  navigateToIssues(event: Event, application: { url: string }): void {
    event.preventDefault()
    this.router.navigate([application.url], { fragment: 'issues' }).then((navigated) => {
      if (!navigated) {
        this.router.navigate([application.url], { fragment: undefined }).then(() => {
          this.router.navigate([application.url], { fragment: 'issues' })
        })
      }
    })
  }
}
